import { useState, useMemo } from 'react';
import { Database, Search, GripVertical, Check } from 'lucide-react';
import { useSaaSModule } from '@/hooks/use-saas-module';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { MMM_ALLOWING_SAAS } from '@/types/field-mapping-flow';
import { useAppStore } from '@/stores/app-store';
import type { MMMCard } from '@/types/field-mapping-flow';

function makeRandomId(length = 7): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function ModulesPanel() {
  const { module: saasModule } = useSaaSModule();
  const appCodeSAAS = useAppStore((s) => s.appCodeSAAS);
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const fieldMappings = useFlowBuilderStore((s) => s.fieldMappings);
  const addCard = useFlowBuilderStore((s) => s.addCard);
  const [searchQuery, setSearchQuery] = useState('');

  // Get the set of module names already added to the selected branch
  const usedModuleNames = useMemo(() => {
    if (!selectedBranchId) return new Set<string>();
    const mapping = fieldMappings[selectedBranchId];
    if (!mapping?.fields || !mapping.isMMM) return new Set<string>();

    const cards = Object.values(mapping.fields).filter(
      (val): val is MMMCard =>
        typeof val === 'object' && val !== null && 'cardId' in val
    );
    return new Set(cards.map((c) => c.saasFieldModuleName));
  }, [selectedBranchId, fieldMappings]);

  const supportedModules = saasModule?.getSupportedModules() || [];
  const isMMMSupported = appCodeSAAS ? (MMM_ALLOWING_SAAS as readonly string[]).includes(appCodeSAAS) : false;

  const filteredModules = supportedModules.filter((m) =>
    m.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // For non-MMM apps, only allow one module per branch
  const hasReachedLimit = !isMMMSupported && usedModuleNames.size > 0;

  const handleModuleClick = (moduleValue: string, moduleLabel: string) => {
    if (!selectedBranchId) return;
    // Prevent duplicates
    if (usedModuleNames.has(moduleValue)) return;
    // Non-MMM apps: only one module allowed
    if (hasReachedLimit) return;

    const cardId = makeRandomId(7);
    const newCard: MMMCard = {
      cardId,
      saasFieldModuleName: moduleValue,
      displayLabel: moduleLabel,
      orderOf: 0,
      fields: {},
      fields_type: {},
      ct: Date.now(),
    };

    addCard(selectedBranchId, newCard);
  };

  const handleDragStart = (e: React.DragEvent, moduleValue: string, moduleLabel: string) => {
    // Prevent dragging already-added modules or when limit reached
    if (usedModuleNames.has(moduleValue) || hasReachedLimit) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('application/flow-module', JSON.stringify({ moduleValue, moduleLabel }));
    e.dataTransfer.effectAllowed = 'move';

    // Custom drag image with proper border-radius
    const target = e.currentTarget as HTMLElement;
    const clone = target.cloneNode(true) as HTMLElement;
    clone.style.position = 'fixed';
    clone.style.top = '-9999px';
    clone.style.left = '-9999px';
    clone.style.width = `${target.offsetWidth}px`;
    clone.style.borderRadius = '8px';
    clone.style.overflow = 'hidden';
    clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    clone.style.opacity = '0.95';
    clone.style.clipPath = 'inset(0 round 8px)';
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, target.offsetWidth / 2, target.offsetHeight / 2);
    // Delay cleanup — browser needs time to capture the drag image
    setTimeout(() => { if (clone.parentNode) clone.parentNode.removeChild(clone); }, 0);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Search */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search modules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Hint when no branch selected */}
      {!selectedBranchId && (
        <div className="mx-3 mb-2 rounded-md bg-muted/50 px-3 py-2">
          <p className="text-[11px] text-muted-foreground">
            Click a condition branch in the flow to select it, then add modules here.
          </p>
        </div>
      )}

      {/* Module list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {filteredModules.length === 0 && (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No modules found
          </p>
        )}

        <div className="space-y-1">
          {filteredModules.map((mod) => {
            const isAlreadyAdded = usedModuleNames.has(mod.value);
            const isDisabled = !selectedBranchId || isAlreadyAdded || (hasReachedLimit && !isAlreadyAdded);

            return (
              <button
                key={mod.value}
                onClick={() => handleModuleClick(mod.value, mod.label)}
                draggable={!!selectedBranchId && !isAlreadyAdded && !hasReachedLimit}
                onDragStart={(e) => handleDragStart(e, mod.value, mod.label)}
                disabled={isDisabled}
                className={`group flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                  isAlreadyAdded
                    ? 'border-border bg-muted/30 cursor-default opacity-60'
                    : 'border-border bg-background hover:bg-muted/50 hover:border-primary/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background disabled:hover:border-border cursor-grab active:cursor-grabbing'
                }`}
              >
                {isAlreadyAdded ? (
                  <div className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-500">
                    <Check className="h-2 w-2 text-white" strokeWidth={3} />
                  </div>
                ) : (
                  <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground" />
                )}
                <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-xs font-medium ${isAlreadyAdded ? 'text-muted-foreground' : 'text-foreground'}`}>
                    {mod.label}
                  </p>
                  <p className={`text-[10px] ${isAlreadyAdded || isDisabled ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
                    {isAlreadyAdded
                      ? 'Added'
                      : hasReachedLimit
                        ? 'Only one module per branch'
                        : selectedBranchId
                          ? 'Click to add to selected branch'
                          : 'Select a branch first'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* MMM info */}
      <div className="border-t border-border px-3 flex items-center h-[45px]">
        <p className="text-[10px] text-muted-foreground">
          {isMMMSupported
            ? 'Multi-module mapping supported. Add multiple modules per branch.'
            : 'One module per branch. Select a module to configure field mapping.'}
        </p>
      </div>
    </div>
  );
}
